import React, { useEffect, useMemo, useRef } from 'react';
import { mountLegacyGrid, unmountLegacyGrid } from './engine';
import LegacyGridHost from './LegacyGridHost';
import './NativeGridView.css';

function buildShellSearch({ sector, px, pxid, impl }) {
  const params = new URLSearchParams();
  params.set('view', 'grid');
  params.set('impl', impl || 'native');
  if (sector) params.set('sector', sector);
  if (px) params.set('px', px);
  if (pxid) params.set('pxid', pxid);
  const s = params.toString();
  return s ? `?${s}` : '';
}

function NativeGridView({ deckId, sector, px, pxid, dataUrls, assetsBaseUrl, impl, pixelPresets, gridTakeoverStatements, registerNavHandlers, motionDisabled }) {
  const rootRef = useRef(null);
  const canvasContainerRef = useRef(null);

  const shellSearch = useMemo(() => buildShellSearch({ sector, px, pxid, impl }), [sector, px, pxid, impl]);

  useEffect(() => {
    let cancelled = false;
    let sloganInterval = null;
    let sloganStartTimeout = null;

    const sectorStatements =
      Array.isArray(gridTakeoverStatements) && gridTakeoverStatements.length > 0
        ? gridTakeoverStatements
        : [
            { text: 'STUDIOS ARE TOTAL INSTITUTIONS', type: 'doom' },
            { text: 'THE FREELANCE LIFESTYLE IS OVER', type: 'doom' },
            { text: 'SHADOW VFX BUNKER', type: 'doom' },
            { text: 'SYNTH WATERMARK REQUIRED', type: 'doom' },
            { text: "RUNNERS CAN'T AFFORD WORK", type: 'doom' },
            { text: 'AUTHENTICITY NOW MANDATORY', type: 'doom' },
            { text: 'INEFFICIENCY IS BANKABLE', type: 'doom' },
            { text: 'THE GATES STAYED OPEN', type: 'hope' },
            { text: '680 UNITS BUILT', type: 'hope' },
            { text: 'GEOGRAPHIC MOBILITY RESTORED', type: 'hope' },
            { text: 'COMPANY TOWN AVOIDED', type: 'hope' },
            { text: 'MOBILITY INCREASED TO 18%', type: 'hope' },
            { text: 'FREELANCE CAREERS VIABLE', type: 'hope' },
            { text: 'HOUSING INTERVENTION WORKED', type: 'hope' },
            { text: 'REGIONAL PRODUCTION THRIVING', type: 'hope' },
            { text: 'CREW SHELTER PROGRAM SUCCESS', type: 'hope' },
            { text: 'AUTHENTIC CONTENT PREMIUM', type: 'hope' }
          ];

    const shuffle = (arr) => {
      const a = arr.slice();
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    let hopeQueue = [];
    let doomQueue = [];
    let showHopeNext = true;

    const refillQueues = () => {
      const hope = sectorStatements.filter((s) => s.type === 'hope');
      const doom = sectorStatements.filter((s) => s.type === 'doom');
      hopeQueue = shuffle(hope);
      doomQueue = shuffle(doom);
    };

    const showSectorTakeover = () => {
      const domRoot = rootRef.current;
      const takeover = domRoot && domRoot.querySelector ? domRoot.querySelector('#sectorTakeover') : null;
      const takeoverText = domRoot && domRoot.querySelector ? domRoot.querySelector('#sectorTakeoverText') : null;
      if (!takeover || !takeoverText) return;

      const isDetail = !!(domRoot && domRoot.dataset && domRoot.dataset.predictionsIsDetailView === '1');
      if (isDetail) return;

      if (hopeQueue.length === 0 || doomQueue.length === 0) refillQueues();

      let item;
      if (showHopeNext && hopeQueue.length > 0) {
        item = hopeQueue.shift();
      } else if (!showHopeNext && doomQueue.length > 0) {
        item = doomQueue.shift();
      } else {
        item = (hopeQueue.length > 0 ? hopeQueue.shift() : doomQueue.shift()) || sectorStatements[0];
      }
      showHopeNext = !showHopeNext;

      takeoverText.textContent = item.text;
      takeover.classList.remove('hope');
      takeover.classList.remove('doom');
      takeover.classList.add(item.type);
      takeover.classList.add('active');

      setTimeout(() => {
        takeover.classList.remove('active');
      }, 2000);
    };

    const applySearch = () => {
      const next = `${window.location.pathname}${shellSearch}`;
      if (window.location.pathname + window.location.search !== next) {
        window.history.replaceState({}, '', next);
      }
    };

    applySearch();

    const stopSlogans = () => {
      if (sloganStartTimeout) {
        clearTimeout(sloganStartTimeout);
        sloganStartTimeout = null;
      }
      if (sloganInterval) {
        clearInterval(sloganInterval);
        sloganInterval = null;
      }
      const domRoot = rootRef.current;
      const takeover = domRoot && domRoot.querySelector ? domRoot.querySelector('#sectorTakeover') : null;
      if (takeover) takeover.classList.remove('active');
    };

    const startSlogans = () => {
      if (sloganInterval) return;
      refillQueues();
      showSectorTakeover();
      sloganInterval = setInterval(showSectorTakeover, 8000);
    };

    const run = async () => {
      const dataUrl = dataUrls && dataUrls.data ? dataUrls.data : '';
      const countermeasuresUrl = dataUrls && dataUrls.countermeasures ? dataUrls.countermeasures : '';

      let resolvedPixelPresets = pixelPresets || null;
      if (!resolvedPixelPresets) {
        const presetsUrl = dataUrls && dataUrls.pixelPresets ? dataUrls.pixelPresets : '';
        if (presetsUrl) {
          try {
            const res = await fetch(presetsUrl);
            if (res.ok) {
              const json = await res.json();
              if (json && typeof json === 'object') resolvedPixelPresets = json;
            }
          } catch (e) {
          }
        }
      }

      const domRoot = rootRef.current || null;
      const canvasContainer = canvasContainerRef.current || (domRoot && domRoot.querySelector ? domRoot.querySelector('#canvas-container') : null);
      await mountLegacyGrid({ canvasContainer, domRoot, startSlogans, stopSlogans, dataUrl, countermeasuresUrl, assetsBaseUrl, pixelPresets: resolvedPixelPresets });

      if (!cancelled) {
        sloganStartTimeout = setTimeout(() => {
          if (!cancelled) startSlogans();
        }, 3000);
      }
    };

    run().catch(() => {});

    return () => {
      cancelled = true;
      try {
        stopSlogans();
        unmountLegacyGrid();
      } catch (e) {
      }
    };
  }, [shellSearch, dataUrls, assetsBaseUrl, pixelPresets, gridTakeoverStatements]);

  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return;

    const btn = rootEl.querySelector ? rootEl.querySelector('#fullscreenToggle') : null;
    if (!btn) return;

    const doc = document;

    const getFullscreenEl = () => {
      return doc.fullscreenElement || doc.webkitFullscreenElement || null;
    };

    const canFullscreen =
      !!(
        doc.fullscreenEnabled ||
        doc.webkitFullscreenEnabled ||
        rootEl.requestFullscreen ||
        rootEl.webkitRequestFullscreen
      );

    if (!canFullscreen) return;

    btn.style.display = 'block';

    const syncLabel = () => {
      btn.textContent = getFullscreenEl() ? 'EXIT' : 'FULL';
    };

    const onToggle = (e) => {
      if (e && e.preventDefault) e.preventDefault();
      if (e && e.stopPropagation) e.stopPropagation();

      const cur = getFullscreenEl();
      if (cur) {
        const exit = doc.exitFullscreen || doc.webkitExitFullscreen;
        if (exit) exit.call(doc);
        return;
      }
      const req = rootEl.requestFullscreen || rootEl.webkitRequestFullscreen;
      if (req) req.call(rootEl);
    };

    syncLabel();
    btn.addEventListener('click', onToggle);

    const onFsChange = () => syncLabel();
    doc.addEventListener('fullscreenchange', onFsChange);
    doc.addEventListener('webkitfullscreenchange', onFsChange);

    return () => {
      btn.removeEventListener('click', onToggle);
      doc.removeEventListener('fullscreenchange', onFsChange);
      doc.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  useEffect(() => {
    if (typeof registerNavHandlers !== 'function') return;

    const click = (id) => {
      const el = document.getElementById(id);
      if (el && typeof el.click === 'function') el.click();
    };

    const isDetail = () => {
      const el = rootRef.current;
      return !!(el && el.dataset && el.dataset.predictionsIsDetailView === '1');
    };

    const prev = () => {
      if (!isDetail()) return;
      click('prev-btn');
    };

    const next = () => {
      if (!isDetail()) return;
      click('next-btn');
    };

    const back = () => {
      if (!isDetail()) return;
      click('grid-btn');
    };

    const mit = () => {
      if (isDetail()) {
        const hudToggle = document.getElementById('hud-mitigation-toggle');
        if (hudToggle && typeof hudToggle.click === 'function') {
          hudToggle.click();
          return;
        }
        click('mitigation-btn');
        return;
      }

      try {
        if (typeof window !== 'undefined' && typeof window.predictionsGridTriggerMarquee === 'function' && !motionDisabled) {
          window.predictionsGridTriggerMarquee('CLICK ME');
        }
      } catch (_e) {
      }
    };

    const lastState = { key: '' };
    const update = () => {
      const detail = isDetail();
      const prevEl = document.getElementById('prev-btn');
      const nextEl = document.getElementById('next-btn');
      const mitigationNote = document.getElementById('hud-mitigation-note');
      const canMitDetail = !!(mitigationNote && mitigationNote.style && mitigationNote.style.display !== 'none');

      const state = {
        canPrev: detail && !(prevEl && prevEl.disabled),
        canNext: detail && !(nextEl && nextEl.disabled),
        canBack: detail,
        canMit: detail ? canMitDetail : true
      };

      const key = JSON.stringify(state);
      if (key === lastState.key) return;
      lastState.key = key;

      registerNavHandlers({ prev, next, back, mit, state });
    };

    update();
    const t = window.setInterval(update, 200);

    return () => {
      window.clearInterval(t);
      try {
        registerNavHandlers(null);
      } catch (_e) {
      }
    };
  }, [motionDisabled, registerNavHandlers]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !el.addEventListener) return;

    const applyCompactUiClass = () => {
      if (typeof window === 'undefined') return;
      const isLandscape = window.matchMedia && window.matchMedia('(orientation: landscape)').matches;
      const isShort = window.innerHeight <= 520;
      if (isLandscape && isShort) el.classList.add('compact-ui');
      else el.classList.remove('compact-ui');
    };

    applyCompactUiClass();
    window.addEventListener('resize', applyCompactUiClass);

    let startX = 0;
    let startY = 0;
    let startT = 0;

    const onTouchStart = (e) => {
      const t = e && e.touches && e.touches[0] ? e.touches[0] : null;
      if (!t) return;
      startX = t.clientX;
      startY = t.clientY;
      startT = Date.now();
    };

    const onTouchEnd = (e) => {
      const t = e && e.changedTouches && e.changedTouches[0] ? e.changedTouches[0] : null;
      if (!t) return;

      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      const dt = Date.now() - startT;

      if (dt > 800) return;
      if (Math.abs(dx) < 44) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.2) return;

      const isDetail = !!(el && el.dataset && el.dataset.predictionsIsDetailView === '1');
      if (!isDetail) return;

      const dir = dx < 0 ? 1 : -1;
      const btn = document.getElementById(dir > 0 ? 'next-btn' : 'prev-btn');
      if (btn && typeof btn.click === 'function') btn.click();
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('resize', applyCompactUiClass);
    };
  }, []);

  return <LegacyGridHost rootRef={rootRef} canvasContainerRef={canvasContainerRef} deckId={deckId} />;
}

export default NativeGridView;
